import React from 'react';
import FacebookLogin from 'react-facebook-login';
import AppleSignin from 'react-apple-signin-auth';
import { Button, Form, Input } from 'antd';
import env from 'utils/env';
import useLoginApi from './useLoginApi';
import ReCAPTCHA from 'react-google-recaptcha';
import { setItem } from 'utils/local-storage';
import './login.css';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import useLoginV2 from 'hooks/useLoginV2';
import GoogleSignin from 'components/Authentication/GoogleSignin/GoogleSignin';
import { auth, googleProvider } from '../../firebase';
import { signInWithPopup, getAdditionalUserInfo } from 'firebase/auth';

const Login: React.FC = () => {
  const { isLoading, login } = useLoginApi();
  const { elsaIcon, captchaSiteKey, internalApiUrl } = env();
  const { t } = useTranslation();

  const recaptchaRef: any = React.useRef();
  const onSubmitWithReCAPTCHA = async (values: any) => {
    const token = await recaptchaRef.current.executeAsync();
    const form = {
      ...values,
      gCaptcha: token
    };
    login(form);
    setItem('email-login', values.email);
  };

  const responseFacebook = (response: any) => {
    const form = {
      facebook_token: response.accessToken
    }
    login(form);
    setItem('email-login', response.email);
  };

  const responseAppleID = (response: any) => {
    const form = {
      apple_token: response.authorization.code
    }
    login(form);
  };

  const responseGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      if (user) {
        const token = await user.getIdToken();
        const additionalUserInfo = getAdditionalUserInfo(result);
        const email = user.email || additionalUserInfo?.profile?.email;
        const form = {
          google_token: token,
          google_email: email && typeof email === 'string' ? email.toString() : ''
        }
        console.log(user, additionalUserInfo);
        login(form);
        if (email && typeof email === 'string') {
          setItem('email-login', email);
        }
      }
    } catch (error) {
      console.error('Error during Google login:', error);
    }
  };

  useLoginV2();
  
  return (
    <div className="signin-container">
      <Helmet>
        <title>Login (登入) - 登入官方網站的ELSA Speak帳戶</title>
        <meta
          name="description"
          content="教學如何登入官方網站上的 ELSA Speak 帳戶，以激活代碼、將免費的ELSA帳戶升級為ELSA Pro、ELSA Premium或Conversation Bundle等"
        />
      </Helmet>
      <div className="logo">
        <img alt="logo" src={elsaIcon} />
      </div>

      <div className="form-container">
        <h1 className="login-title">{t('login.page_title')}</h1>
        <FacebookLogin
          appId="817185751731429"
          fields="name, email"
          scope="email"
          icon="fa-facebook"
          cssClass="btn-login-facebook"
          callback={responseFacebook}
          textButton={t('login.login_facebook')}
        />

        <AppleSignin
          authOptions={{
            clientId: "com.elsaspeak.speechanalyzer",
            scope: "email",
            redirectURI: internalApiUrl,
            state: "state",
            nonce: "[NONCE]",
            usePopup: true,
          }}
          uiType="dark"
          noDefaultStyle={true}
          className="apple-auth-btn"
          onSuccess={(response: any) => responseAppleID(response)}
          onError={(error: any) => console.error(error)}
        />

        <GoogleSignin onClick={responseGoogle}>Login with Google</GoogleSignin>

        <div className="form-divider">
          <span>OR</span>
        </div>
        
        <Form
          layout="vertical"
          onFinish={onSubmitWithReCAPTCHA}
          className="form"
        >
          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={captchaSiteKey}
          />
          <Form.Item
            name="email"
            rules={[
              {
                type: 'email',
                required: true,
                message: '此欄位是必需的'
              }
            ]}
          >
            <Input placeholder="電子信箱" />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              {
                required: true,
                message: '此欄位是必需的'
              }
            ]}
          >
            <Input.Password placeholder="ELSA 帳戶密碼" />
          </Form.Item>

          <Button
            htmlType="submit"
            className="submit-button"
            loading={isLoading}
          >
            {t('login.login')}
          </Button>
        </Form>

        <div className="form-login-footer">
          <Link to={`/${t('slug.forget_password')}`}>
            <button className="footer-link" >{t('login.forgot_password')}</button>
          </Link>
          <Link to={`/${t('slug.signup')}`}>
            <button className="footer-link" >
            {t('signup.signup')}
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
